<template>
  <div class="bg-bg font-sans">
    <!-- Hero -->
    <section class="border-b border-solid border-line bg-surface">
      <div class="mx-auto grid max-w-screen-xl items-center gap-10 px-4 py-16 lg:grid-cols-[1.04fr_0.96fr] lg:gap-14 lg:py-[62px]">
        <div>
          <p class="mb-4.5 font-display text-xs font-bold uppercase tracking-eyebrow text-accent">
            {{ $t('pages.journalists.sections.header.eyebrow') }}
          </p>
          <h1 class="mb-4 font-display text-5xl font-extrabold leading-none tracking-display text-ink">
            {{ $t('pages.journalists.sections.header.be_the_first') }}
          </h1>
          <p class="mb-7 max-w-md font-sans text-lg leading-relaxed text-muted">
            {{ $t('pages.journalists.sections.header.intro') }}
          </p>
          <div class="flex flex-wrap items-center gap-3">
            <button
              v-if="!$hasAccess('reader')"
              type="button"
              class="cursor-pointer rounded-full border-none bg-accent px-[26px] py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90"
              data-testid="ForJournalists-CreateMediaAccount"
              @click="openRegisterModal"
            >
              {{ $t('pages.journalists.sections.header.create_media_account') }}
            </button>
            <router-link
              :to="$i18nRoute({ name: 'home' })"
              class="rounded-full border border-solid border-outline bg-white px-[22px] py-3.5 text-sm font-bold text-ink transition-colors hover:text-accent"
            >
              {{ $t('pages.journalists.sections.header.browse_newsrooms') }} →
            </router-link>
          </div>
          <p class="mt-5 font-display text-sm font-semibold text-muted-faint">
            {{ $t('pages.journalists.sections.header.free_for_media') }}
          </p>
        </div>

        <your-alerts />
      </div>
    </section>

    <!-- Cross strip: organisation / brand CTA -->
    <section class="border-b border-solid border-line bg-bg">
      <div class="mx-auto flex max-w-screen-xl flex-col items-start gap-4 px-4 py-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p class="font-display text-lg font-extrabold tracking-tight text-ink">
            {{ $t('pages.journalists.sections.organisation.organisation_or_brand') }}
          </p>
          <p class="mt-[3px] font-display text-sm font-medium text-muted">
            {{ $t('pages.journalists.sections.organisation.get_customized_channel') }}
          </p>
        </div>
        <button
          v-if="!$hasAccess('reader')"
          type="button"
          class="cursor-pointer whitespace-nowrap rounded-full border border-solid border-outline bg-white px-5 py-3 text-sm font-bold text-ink transition-colors hover:text-accent"
          @click="openRegisterModal"
        >
          {{ $t('pages.journalists.sections.organisation.signup_for_free') }}
        </button>
      </div>
    </section>

    <!-- Features -->
    <section class="border-b border-solid border-line bg-white">
      <div class="mx-auto max-w-screen-xl px-4 py-16">
        <h2 class="mb-8 font-display text-3xl font-extrabold tracking-heading text-ink">
          {{ $t('pages.journalists.sections.features.title') }}
        </h2>
        <div class="grid gap-[22px] md:grid-cols-2">
          <div
            v-for="(block, index) in featureBlocks"
            :key="index"
            class="rounded-card border border-solid border-line bg-white p-7 shadow-sm"
          >
            <p
              v-if="block.eyebrow"
              class="mb-[10px] font-display text-xs font-bold uppercase tracking-widest text-accent"
            >
              {{ block.eyebrow }}
            </p>
            <p class="mb-2 font-display text-2xl font-extrabold tracking-tight text-ink">
              {{ block.subtitle }}
            </p>
            <p class="font-sans text-sm leading-relaxed text-muted">
              {{ block.text }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Topics -->
    <section
      v-if="mainCategories.length"
      class="border-b border-solid border-line bg-bg"
    >
      <div class="mx-auto max-w-screen-lg px-4 py-12 text-center">
        <h3 class="mb-4.5 font-display text-[22px] font-extrabold tracking-tight text-ink">
          {{ $t('pages.journalists.sections.topics.title') }}
        </h3>
        <div class="flex flex-wrap justify-center gap-[10px]">
          <router-link
            v-for="category in mainCategories"
            :key="category.id"
            :to="$i18nRoute({ name: 'home', query: { main_category: category.id } })"
            target="_blank"
            rel="noopener"
            class="rounded-full border border-solid border-line bg-white px-4.5 py-[9px] text-sm font-semibold text-ink transition-colors hover:text-accent"
          >
            {{ category.name }}
          </router-link>
        </div>
      </div>
    </section>

    <!-- Dark CTA band -->
    <section class="border-b border-solid border-white/10 bg-surface-dark px-4 py-[58px] text-center">
      <h2 class="mb-2 font-display text-4xl font-extrabold leading-tight tracking-heading text-white">
        {{ $t('pages.journalists.sections.header.discover_next_story') }}
      </h2>
      <p class="mb-6 font-sans text-base text-white/60">
        {{ $t('pages.journalists.sections.cta.subtitle') }}
      </p>
      <button
        v-if="!$hasAccess('reader')"
        type="button"
        class="inline-block cursor-pointer rounded-full border-none bg-accent px-[30px] py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90"
        @click="openRegisterModal"
      >
        {{ $t('pages.journalists.sections.header.create_media_account') }}
      </button>
    </section>

    <!-- Register modal — el-dialog kept on Element UI (restyled surround only), flagged for INIT-2026-07-element-ui-removal -->
    <el-dialog
      :title="$t('pages.journalists.sections.header.create_media_account')"
      :visible.sync="registerModalVisible"
      custom-class="ForJournalistsRegisterDialog"
      width="480px"
      destroy-on-close
      data-testid="ForJournalists-RegisterModal"
    >
      <journalist-register-form />
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import YourAlerts from '@/components/marketing/YourAlerts'
import JournalistRegisterForm from '@/components/signup/JournalistRegisterForm.vue'

export default {
  name: 'ForJournalists',

  components: {
    YourAlerts,
    JournalistRegisterForm
  },

  data () {
    return {
      // "Create media account" opens the register modal in place instead of routing
      // to the old standalone /register-journalist page (S17).
      registerModalVisible: false
    }
  },

  computed: {
    ...mapGetters(['mainCategories']),

    /**
     * The four feature cards, sourced from the existing i18n blocks.
     * The i18n titles carry a leading "N. " ordinal that we strip for the eyebrow;
     * an empty title (e.g. da) drops the eyebrow.
     * @returns {{ eyebrow: string, subtitle: string, text: string }[]}
     */
    featureBlocks () {
      const blocks = this.$t('pages.journalists.sections.connecting_stories.blocks')
      return blocks.map(block => ({
        eyebrow: block.title.replace(/^\d+\.\s*/, ''),
        subtitle: block.subtitle,
        text: block.text
      }))
    }
  },

  created () {
    // Topics chips are decorative — if categories fail to load the section
    // simply hides (v-if="mainCategories.length"), so swallow the error.
    this.$store.dispatch('shouldFetchCategories').catch(() => {})
  },

  mounted () {
    // Deep-link support: /register-journalist redirects here with ?register=1 so
    // external/marketing links still land on a registration experience (the modal),
    // rather than 404ing. A logged-in reader already has an account, so gate the
    // auto-open the same way the CTAs are gated.
    if (this.$route.query.register && !this.$hasAccess('reader')) {
      this.registerModalVisible = true
    }
  },

  methods: {
    openRegisterModal () {
      this.registerModalVisible = true
    }
  }
}
</script>

<style lang="scss">
/* Register modal: keep a tall form usable — scroll inside the dialog body on short
   viewports, and go near-full-width on mobile. el-dialog stays on Element UI
   (flagged for INIT-2026-07-element-ui-removal); we only touch the surround. */
.ForJournalistsRegisterDialog {
  @media (max-width: 600px) {
    width: 92% !important;
  }

  .el-dialog__body {
    max-height: 70vh;
    overflow-y: auto;
    padding-top: 1rem;
  }
}
</style>
